import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "@emotion/react/jsx-runtime";
import { Fragment } from 'react';
import { playerIdNameParser } from '../../api/inbound/commonInbounds/utils/playerInboundUtils';
import { encodedStringParser } from '../../api/inbound/inboundParsers';
import { isConfigFlagEnabled } from '../../config';
import { range } from '../../utils/utils';
import { HtmlAreaExternalLinkButton } from './htmlAreaExternalLinkButton';
import { HtmlAreaPlayerInfoButton } from './htmlAreaPlayerInfoButton';
import { HtmlAreaRoomLinkButton } from './htmlAreaRoomLinkButton';
import { HtmlAreaShowPowerPlayerStatusButton } from './htmlAreaShowPowerPlayerStatusButton';
import { HtmlAreaSocketSendButton } from './htmlAreaSocketSendButton';
import { getEmojiFromChildNode } from './htmlEmojiUtils';
import { SmileysTextChatItemDisplay } from './smileysTextChatItemDisplay';
function createButtonElement(childNode, key) {
    const buttonLabel = (childNode.textContent || '').trim();
    if (!buttonLabel) {
        return null;
    }
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const href = childNode.href;
    if (!href) {
        return null;
    }
    const firstCommaIndex = href.indexOf(',');
    const asfunctionName = firstCommaIndex !== -1 ? href.substring(0, firstCommaIndex) : href;
    const argsString = firstCommaIndex !== -1 ? href.substring(firstCommaIndex + 1) : '';
    switch (asfunctionName) {
        case 'tj:request-join-room': {
            const roomId = Number(argsString);
            return _jsx(HtmlAreaRoomLinkButton, { label: buttonLabel, roomId: roomId }, key);
        }
        case 'tj:request-player-info': {
            const player = playerIdNameParser(argsString);
            return _jsx(HtmlAreaPlayerInfoButton, { label: buttonLabel, player: player }, key);
        }
        case 'asfunction:PokerCommon.socket_send': {
            const outbound = argsString;
            // Only create a button for socket_send outbounds specifically supported in the client.
            // TODO: Update this as more outbounds can be supported.
            const supportedSocketSendOutboundPrefixes = ['lounge:', 'llounge:', 'genchat:/option_inc', 'config_setting_set:'];
            if (supportedSocketSendOutboundPrefixes.some((supportedSocketSendOutboundPrefix) => outbound.startsWith(supportedSocketSendOutboundPrefix))) {
                return _jsx(HtmlAreaSocketSendButton, { label: buttonLabel, outbound: outbound }, key);
            }
            // TODO: Handle more socket_send types.
            break;
        }
        case 'asfunction:PokerCommon.show_upgrade_page': {
            return _jsx(HtmlAreaShowPowerPlayerStatusButton, { label: buttonLabel }, key);
        }
        case 'asfunction:PokerCommon.pop_combo': {
            // for example: `asfunction:PokerCommon.pop_combo,http%3A%2F%2Fwww.triplejack.com%2F,,,,1`
            // (note the URL is encoded)
            // TODO: Support showing warning dialog.
            // TODO: Support POSTing any provided data.
            const [encodedUrl] = argsString.split(',');
            const url = encodedStringParser(encodedUrl);
            return _jsx(HtmlAreaExternalLinkButton, { url: url });
        }
        case 'asfunction:PokerCommon.pop': {
            // for example: `asfunction:PokerCommon.pop,https://www.triplejack.com/`
            // (note the URL is NOT encoded, differing from PokerCommon.pop_combo)
            const [url] = argsString.split(',');
            return _jsx(HtmlAreaExternalLinkButton, { url: url });
        }
        // TODO: Handle more href types.
    }
    console.warn(`href not handled in html parser: ${href}`);
    return null;
}
function getNumSpacesResult(text, isReplacingAtStart) {
    let numSpaces = 0;
    let spacesStr = ' ';
    const checkerFn = isReplacingAtStart ? text.startsWith.bind(text) : text.endsWith.bind(text);
    while (checkerFn(spacesStr)) {
        numSpaces++;
        spacesStr += ' ';
    }
    return {
        trimmedText: isReplacingAtStart ? text.substring(numSpaces) : text.substring(0, text.length - numSpaces),
        numSpaces
    };
}
/**
 * For the provided text, return the string after trimming off any spaces from the start / end, plus a count of how many
 * spaces were removed from each end, so they can be replaced by the corresponding number of nbsp; characters when rendered.
 */
function getNbspResult(originalText) {
    if (!originalText) {
        return null;
    }
    const startSpacesResult = getNumSpacesResult(originalText, true);
    let trimmedText = startSpacesResult.trimmedText; // removed start spaces
    const endSpacesResult = getNumSpacesResult(trimmedText, false);
    trimmedText = endSpacesResult.trimmedText; // removed end spaces
    return { trimmedText, numStartSpaces: startSpacesResult.numSpaces, numEndSpaces: endSpacesResult.numSpaces };
}
function getSmileysTextReactNodeFromChildNode(childNode) {
    if (childNode.nodeName.toLowerCase() !== 'font') {
        return;
    }
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    if (childNode.face?.toLowerCase() !== 'smileystext') {
        return;
    }
    if (childNode.childNodes.length !== 1) {
        return;
    }
    const innerNode = childNode.childNodes[0];
    if (innerNode.nodeName.toLowerCase() !== '#text') {
        return;
    }
    const innerValue = innerNode.nodeValue?.trim();
    if (!innerValue) {
        return;
    }
    let trimmedInnerValue = innerValue;
    while (trimmedInnerValue.startsWith('(')) {
        trimmedInnerValue = trimmedInnerValue.substring(1);
    }
    while (trimmedInnerValue.endsWith(')')) {
        trimmedInnerValue = trimmedInnerValue.substring(0, trimmedInnerValue.length - 1);
    }
    return _jsx(SmileysTextChatItemDisplay, { text: trimmedInnerValue });
}
function getChildNodesElements(childNodes, opts) {
    return Array.from(childNodes).map((childNode, i) => {
        if (childNode.nodeName.toLowerCase() === '#text') {
            // This has some nuance to it. The server-provided HTML strings sometimes have spaces that are intended to be rendered,
            // and which were rendered in Flash's HTML renderer. However, spaces that are at the ends of text content within an element
            // are not typically rendered by the browser, so things can look squashed together when some spaces are ignored.
            // Here, I'm replacing any sequence of spaces that exist in a text element at the start and/or end with the corresponding
            // number of &nbsp; to enforce these spaces. To make things weird, though, React doesn't really honor &nbsp; and replaces
            // it with what seems to be a normal space surrounded by newlines, which in effect gives us the desired result.
            // TODO: Revisit if this can be handled in a more clear way (without nbsp at all, which is kind of a hack), and determine
            // if we should force spaces to render within all node types, not just '#text'.
            const nbspResult = getNbspResult(childNode.nodeValue);
            if (!nbspResult) {
                return null;
            }
            const { trimmedText, // start/end spaces have been removed
            numStartSpaces, numEndSpaces } = nbspResult;
            return (_jsxs("span", { children: [range(numStartSpaces).map((_, j) => (_jsx("span", { children: "\u00A0" }, j))), trimmedText, range(numEndSpaces).map((_, j) => (_jsx("span", { children: "\u00A0" }, j)))] }, i));
        }
        if (childNode.nodeName.toLowerCase() === 'font') {
            const emoji = isConfigFlagEnabled('clientSideEmojiReplacement') && getEmojiFromChildNode(childNode);
            if (emoji) {
                return _jsx("span", { children: emoji }, i);
            }
            const smileysTextReactNode = getSmileysTextReactNodeFromChildNode(childNode);
            if (smileysTextReactNode) {
                return _jsx("span", { children: smileysTextReactNode }, i);
            }
            if (!opts.honorFontTags) {
                // ignore the font tag, but process its children:
                return _jsx(Fragment, { children: getChildNodesElements(childNode.childNodes, opts) }, i);
            }
            const style = {};
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            const color = childNode.color;
            if (color) {
                style.color = opts.fontColorReplacements[color] || color;
            }
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            const face = childNode.face;
            if (face) {
                if (face.toLowerCase() === 'fffvancouver') {
                    // replace the Flash-specific 'fffvancouver' font with simply larger text:
                    style.fontSize = '120%';
                }
                else {
                    style.fontFamily = face;
                }
            }
            return (_jsx("span", { style: style, children: getChildNodesElements(childNode.childNodes, opts) }, i));
        }
        if (childNode.nodeName.toLowerCase() === 'br') {
            if (opts.honorBreakTags) {
                return _jsx("br", {}, i);
            }
            else {
                return ' ';
            }
        }
        if (childNode.nodeName.toLowerCase() === 'a') {
            // if we encounter an <a> tag, ignore all contained HTML, but extract/parse the href and label into a button
            return createButtonElement(childNode, i);
        }
        return null;
    });
}
function createElements(html, opts) {
    const domParser = new DOMParser();
    const domParserDocument = domParser.parseFromString(html, 'text/html');
    return getChildNodesElements(domParserDocument.body.childNodes, opts);
}
export const HtmlRenderer = ({ html, honorBreakTags = true, honorFontTags = true, fontColorReplacements = {} }) => {
    if (!html) {
        return null;
    }
    const opts = { honorBreakTags, honorFontTags, fontColorReplacements };
    // TODO: Figure out if the corresponding elements for a given HTML input can be memoized across instances.
    // In other words, can we prevent having to parse and rebuild the tree of elements for an HTML string,
    // for example as chat messages come in and cause re-rendering of the entire chat panel?
    return _jsx(_Fragment, { children: createElements(html, opts) });
};
